import React, { createContext, useRef, useContext, useState } from 'react';

interface IExpandContextProps {
  children?: React.ReactNode;
  defaultExpanded?: boolean;
}

interface IExpandContextValues {
  isOpen: boolean;
  toggle: () => void;
}

const ExpandContext = createContext<IExpandContextValues>({
  isOpen: false,
  toggle: () => null,
});

const ExpandProvider: React.FC<IExpandContextProps> = ({
  defaultExpanded = false,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  const toggle = () => setIsOpen((prev) => !prev);

  return (
    <ExpandContext.Provider value={{ isOpen, toggle }}>
      {children}
    </ExpandContext.Provider>
  );
};

interface ExpandPanelProps {
  /**
   *
   */

  children: React.ReactNode;

  /**
   * Flag to force the summary bloc as inline
   */
  isInline?: boolean;
  /**
   * Flag to activate the autofocus
   */
  autofocus?: boolean;
  /**
   * Flag to expand by default
   */
  defaultExpanded?: boolean;
  /**
   * String for E2E tests
   */
  dataTestId?: string;
}

const handleAutofocus = (e) => {
  const detailsElement = e.currentTarget as HTMLDetailsElement;

  if (detailsElement.open) {
    const focusable = detailsElement.querySelector(
      'a:not([disabled]), button:not([disabled]), input[type=text]:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])',
    ) as HTMLElement;

    focusable?.focus();
  }
};

const Details = ({
  children,
  isInline = false,
  autofocus = false,
  defaultExpanded = false,
  dataTestId,
}: ExpandPanelProps) => {
  const defaultOpen = useRef(defaultExpanded);

  return (
    <ExpandProvider defaultExpanded={defaultExpanded}>
      <details
        onToggle={autofocus ? handleAutofocus : undefined}
        open={defaultOpen.current}
        data-testid={dataTestId}
      >
        {children}
      </details>
      <style jsx>{`
        details :global(summary) {
          display: ${isInline ? 'inline-flex' : 'flex'};
        }
      `}</style>
    </ExpandProvider>
  );
};

const Summary = ({
  children,
  UNSAFE_className,
}: {
  children: React.ReactNode;
  UNSAFE_className?: string;
}) => {
  const { toggle } = useContext(ExpandContext);

  return (
    <>
      <summary
        onClick={toggle}
        className={`${UNSAFE_className ? UNSAFE_className : ''}
        tw-flex tw-w-full tw-cursor-pointer tw-list-none tw-items-center tw-justify-between`}
      >
        {children}
      </summary>
      <style jsx>{`
        summary::-webkit-details-marker {
          display: none;
        }
      `}</style>
    </>
  );
};

const ExpandIcon = () => {
  const { isOpen } = useContext(ExpandContext);

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`tw-ease tw-transform tw-transition tw-duration-500 ${
        isOpen ? '-tw-rotate-180' : 'tw-rotate-0'
      }`}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.64814 9.20767C5.9329 8.92291 6.39458 8.92291 6.67934 9.20767L11.9971 14.5254L17.3148 9.20767C17.5996 8.92291 18.0612 8.92291 18.346 9.20767C18.6308 9.49243 18.6308 9.95411 18.346 10.2389L12.5127 16.0722C12.3759 16.2089 12.1905 16.2858 11.9971 16.2858C11.8037 16.2858 11.6182 16.2089 11.4815 16.0722L5.64814 10.2389C5.36338 9.95411 5.36338 9.49243 5.64814 9.20767Z"
        fill="black"
      />
    </svg>
  );
};

export default { Details, Summary, ExpandIcon };
